import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    AvatarGroup,
    Button,
    Empty,
    Card
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon,
    FileIcon,
    PlusIcon
} from "../../anatomy/icons";

const NAME = "Empty";

const USAGE_CODE = `<Empty
    variant="outline"
>
    <Empty.Header>
        <Empty.Media
            icon={FileIcon}
        />
        <Empty.Title>
            No projects yet
        </Empty.Title>
        <Empty.Description>
            Create your first project or import one you already have.
        </Empty.Description>
    </Empty.Header>
    <Empty.Content>
        <Button
            onPress={handleCreate}
            title="Create project"
            icon={PlusIcon}
            size="small"
        />
        <Button
            onPress={handleImport}
            variant="outline"
            type="neutral"
            title="Import"
            size="small"
        />
        <Button
            onPress={handleDocs}
            title="Read the docs"
            variant="link"
            size="small"
        />
    </Empty.Content>
</Empty>`;

const CARD_CODE = `<Card.Content>
    <Empty
        size="small"
    >
        <Empty.Header>
            <Empty.Media
                icon={SearchIcon}
            />
            <Empty.Title>
                No results
            </Empty.Title>
            <Empty.Description>
                Nothing matches "invoice 2024".
            </Empty.Description>
        </Empty.Header>
        <Empty.Content>
            <Button
                onPress={handleClearSearch}
                title="Clear search"
                variant="outline"
                type="neutral"
            />
        </Empty.Content>
    </Empty>
</Card.Content>`;

const MEDIA_CODE = `<Empty>
    <Empty.Header>
        <Empty.Media>
            <AvatarGroup
                avatars={members}
            />
        </Empty.Media>
        <Empty.Title>
            Nobody on the team yet
        </Empty.Title>
    </Empty.Header>
    <Empty.Content>
        <Button
            onPress={handleInvite}
            title="Invite"
        />
    </Empty.Content>
</Empty>`;

const TYPES = [
    "neutral",
    "primary",
    "success",
    "warning",
    "danger",
    "info"
] as const;

const RETRY_DELAY = 1400;

const GRID_STYLE: CSSProperties = {
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "wrap",
    display: "flex"
};

const COLUMN_STYLE: CSSProperties = {
    alignItems: "flex-start",
    flexDirection: "column",
    display: "flex"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("emptyPage.variantTitle"),
        id: "variants"
    },
    {
        title: t("emptyPage.mediaTitle"),
        id: "media"
    },
    {
        title: t("emptyPage.typeTitle"),
        id: "types"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const EmptyPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        status,
        setStatus
    ] = useState<"error" | "loading" | "success">("error");

    useEffect(() => {
        if(status !== "loading") {
            return;
        }

        const timer = window.setTimeout(() => setStatus("success"), RETRY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        status
    ]);

    const isSuccess = status === "success";

    const handleRetry = () => setStatus("loading");

    const handleReset = () => setStatus("error");

    const handleNothing = () => undefined;

    const members = [
        "emptyPage.member1",
        "emptyPage.member2",
        "emptyPage.member3"
    ].map((key) => ({
        title: t(key)
    }));

    const renderLabeled = (label: string, content: ReactNode) => <div
        style={{
            ...COLUMN_STYLE,
            gap: spaces.spacingSm
        }}
        key={label}
    >
        <InlineCode>
            {label}
        </InlineCode>
        {content}
    </div>;

    const renderProjects = () => <Empty
        variant="outline"
    >
        <Empty.Header>
            <Empty.Media
                icon={FileIcon}
            />
            <Empty.Title>
                {t("emptyPage.noProjects")}
            </Empty.Title>
            <Empty.Description>
                {t("emptyPage.noProjectsText")}
            </Empty.Description>
        </Empty.Header>
        <Empty.Content>
            <Button
                title={t("emptyPage.createProject")}
                onPress={handleNothing}
                icon={PlusIcon}
                size="small"
            />
            <Button
                title={t("emptyPage.import")}
                onPress={handleNothing}
                variant="outline"
                type="neutral"
                size="small"
            />
            <Button
                title={t("emptyPage.readDocs")}
                onPress={handleNothing}
                variant="link"
                size="small"
            />
        </Empty.Content>
    </Empty>;

    return <Article>
        <ComponentHeader
            lead={t("emptyPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("emptyPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("emptyPage.overviewNote")}
            </Paragraph>
            <Preview
                caption={t("emptyPage.overviewCaption")}
                isStacked={true}
            >
                {renderProjects()}
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("emptyPage.variantTitle")}
            id="variants"
        >
            <Paragraph>
                {t("emptyPage.variantText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Card>
                    <Card.Header
                        isDivided={true}
                    >
                        <Card.Title>
                            {t("emptyPage.invoices")}
                        </Card.Title>
                        <Card.Description>
                            {t("emptyPage.invoicesPeriod")}
                        </Card.Description>
                    </Card.Header>
                    <Card.Content>
                        <Empty
                            size="small"
                        >
                            <Empty.Header>
                                <Empty.Media
                                    icon={SearchIcon}
                                />
                                <Empty.Title>
                                    {t("emptyPage.noResults")}
                                </Empty.Title>
                                <Empty.Description>
                                    {t("emptyPage.noResultsText")}
                                </Empty.Description>
                            </Empty.Header>
                            <Empty.Content>
                                <Button
                                    title={t("emptyPage.clearSearch")}
                                    onPress={handleNothing}
                                    variant="outline"
                                    type="neutral"
                                    size="small"
                                />
                            </Empty.Content>
                        </Empty>
                    </Card.Content>
                </Card>
            </Preview>
            <CodeBlock
                code={CARD_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("emptyPage.mediaTitle")}
            id="media"
        >
            <Paragraph>
                {t("emptyPage.mediaText")}
            </Paragraph>
            <Preview>
                <Empty>
                    <Empty.Header>
                        <Empty.Media>
                            <AvatarGroup
                                avatars={members}
                            />
                        </Empty.Media>
                        <Empty.Title>
                            {t("emptyPage.noTeam")}
                        </Empty.Title>
                        <Empty.Description>
                            {t("emptyPage.noTeamText")}
                        </Empty.Description>
                    </Empty.Header>
                    <Empty.Content>
                        <Button
                            title={t("emptyPage.invite")}
                            onPress={handleNothing}
                            icon={PlusIcon}
                            size="small"
                        />
                    </Empty.Content>
                </Empty>
            </Preview>
            <CodeBlock
                code={MEDIA_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("emptyPage.typeTitle")}
            id="types"
        >
            <Paragraph>
                {t("emptyPage.typeText")}
            </Paragraph>
            <Preview
                caption={t("emptyPage.typeCaption")}
            >
                <div
                    style={{
                        ...GRID_STYLE,
                        gap: spaces.spacingLg
                    }}
                >
                    {
                        TYPES.map((type) => renderLabeled(`type="${type}"`, <div
                            style={{
                                width: shapeConfigs.drawerWidth
                            }}
                        >
                            <Empty
                                size="small"
                                type={type}
                            >
                                <Empty.Header>
                                    <Empty.Media />
                                    <Empty.Title>
                                        {type}
                                    </Empty.Title>
                                </Empty.Header>
                            </Empty>
                        </div>))
                    }
                </div>
            </Preview>
            <Preview
                caption={t("emptyPage.errorCaption")}
                isStacked={true}
            >
                <Empty
                    type={isSuccess ? "success" : "danger"}
                    isLoading={status === "loading"}
                    variant="card"
                >
                    <Empty.Header>
                        <Empty.Media />
                        <Empty.Title>
                            {isSuccess ? t("emptyPage.connectedTitle") : t("emptyPage.failedTitle")}
                        </Empty.Title>
                        <Empty.Description>
                            {isSuccess ? t("emptyPage.connectedText") : t("emptyPage.failedText")}
                        </Empty.Description>
                    </Empty.Header>
                    <Empty.Content>
                        <Button
                            title={isSuccess ? t("emptyPage.startOver") : t("emptyPage.retry")}
                            onPress={isSuccess ? handleReset : handleRetry}
                            type={isSuccess ? "success" : "danger"}
                            size="small"
                        />
                    </Empty.Content>
                </Empty>
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("emptyPage.sizesText")}
            </Paragraph>
            <Preview>
                <div
                    style={{
                        ...GRID_STYLE,
                        gap: spaces.spacingLg
                    }}
                >
                    {
                        [
                            "medium",
                            "small"
                        ].map((size) => renderLabeled(`size="${size}"`, <div
                            style={{
                                width: shapeConfigs.drawerWidth
                            }}
                        >
                            <Empty
                                size={size as "small" | "medium"}
                                variant="card"
                            >
                                <Empty.Header>
                                    <Empty.Media
                                        icon={FileIcon}
                                    />
                                    <Empty.Title>
                                        {t("emptyPage.noRecords")}
                                    </Empty.Title>
                                    <Empty.Description>
                                        {t("emptyPage.noRecordsText")}
                                    </Empty.Description>
                                </Empty.Header>
                            </Empty>
                        </div>))
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("emptyPage.statesText")}
            </Paragraph>
            <Preview>
                <div
                    style={{
                        ...GRID_STYLE,
                        gap: spaces.spacingLg
                    }}
                >
                    {
                        [
                            true,
                            false
                        ].map((isLoading) => renderLabeled(`isLoading={${isLoading}}`, <div
                            style={{
                                width: shapeConfigs.drawerWidth
                            }}
                        >
                            <Empty
                                isLoading={isLoading}
                                variant="card"
                            >
                                <Empty.Header>
                                    <Empty.Media
                                        icon={FileIcon}
                                    />
                                    <Empty.Title>
                                        {t("emptyPage.noReports")}
                                    </Empty.Title>
                                    <Empty.Description>
                                        {t("emptyPage.noReportsText")}
                                    </Empty.Description>
                                </Empty.Header>
                                <Empty.Content>
                                    <Button
                                        title={t("emptyPage.createReport")}
                                        onPress={handleNothing}
                                        icon={PlusIcon}
                                    />
                                </Empty.Content>
                            </Empty>
                        </div>))
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("emptyPage.a11yHeading"),
                    t("emptyPage.a11yMedia"),
                    t("emptyPage.a11yContent"),
                    t("emptyPage.a11yLoading"),
                    t("emptyPage.a11yRole")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"plain\" | \"outline\" | \"card\"",
                        description: t("emptyPage.propVariant"),
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        type: "\"neutral\" | \"primary\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("emptyPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("emptyPage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("emptyPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("emptyPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("emptyPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph>
                {t("emptyPage.partsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("emptyPage.propMediaIcon"),
                        type: "NCoreUIKitIcon",
                        name: "Media icon"
                    },
                    {
                        description: t("emptyPage.propMediaIconSize"),
                        name: "Media iconSize",
                        type: "number"
                    },
                    {
                        description: t("emptyPage.propMediaChildren"),
                        name: "Media children",
                        type: "ReactNode"
                    },
                    {
                        description: t("emptyPage.propTitleChildren"),
                        name: "Title children",
                        type: "ReactNode"
                    },
                    {
                        description: t("emptyPage.propDescriptionChildren"),
                        name: "Description children",
                        type: "ReactNode"
                    },
                    {
                        description: t("emptyPage.propContentChildren"),
                        name: "Content children",
                        type: "ReactNode"
                    },
                    {
                        name: "Header, Media, Title, Description, Content style",
                        description: t("emptyPage.propPartStyle"),
                        type: "StyleProp<ViewStyle | TextStyle>"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("emptyPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default EmptyPage;
